import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { Filter } from 'components/FilterDrawer/FilterDrawer.config'
import ActionsBar from 'containers/VerticalPage/common/Company/OverviewTab/ActionsBar'
import EmployeeOverviewChart from 'containers/VerticalPage/common/Company/OverviewTab/EmployeeOverviewChart'
import { useOverviewTabQuery } from 'containers/VerticalPage/common/Company/OverviewTab/useOverviewTabQuery'
import {
  filtersInitialState,
  formatChartData,
  formatFilterOptions,
} from 'containers/VerticalPage/common/Company/OverviewTab/utils'
import { useDebounce } from 'hooks/useDebounce'
import { useFilterOptionsQuery } from 'hooks/useFilterOptionsQuery'
import React, { useContext, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { VerticalContext } from 'store/verticals/Context'
import styles from './styles'

interface OverviewTabProps {
  companyAggsIndices: string
}

const OverviewTab = ({ companyAggsIndices }: OverviewTabProps) => {
  const { verticalEntity } = useContext(VerticalContext)
  const { caseId } = useParams() as { caseId: string }

  const [activeFilters, setActiveFilters] = useState<Filter[]>([])
  const activeFiltersDebounced = useDebounce(activeFilters, 500)

  const { filterOptions } = useFilterOptionsQuery({
    verticalIndices: companyAggsIndices,
    currentQueryKey: 'company-filter-options-key',
    filtersInitialState,
    entityId: caseId,
    entityIdField: 'entityId',
  })

  const formattedFilterOptions = useMemo(() => formatFilterOptions(filterOptions), [filterOptions])

  // Cast totals to access company-overview specific properties
  const totals = verticalEntity?.totals as
    | ({ employeesLocal?: number; employeesAbroad?: number } & Record<string, unknown>)
    | undefined

  const { data: employeeOvertimeChartData } = useOverviewTabQuery({
    currentQueryKey: 'company-charts-data',
    payload: {
      caseId,
      zoneEndpoint: companyAggsIndices,
      localCountry: verticalEntity?.overviewDetails?.localJurisdiction,
      filters: activeFiltersDebounced,
      employeesLocal: totals?.employeesLocal,
      employeesAbroad: totals?.employeesAbroad,
    },
  })

  const formattedChartData = useMemo(() => formatChartData(employeeOvertimeChartData), [employeeOvertimeChartData])

  const tabHeaderRef = useRef<HTMLDivElement>(null)

  return (
    <Stack height='100%' minHeight='100%'>
      <Stack direction='row' alignItems='center' ref={tabHeaderRef}>
        <Typography sx={styles.Title}>Employees Overtime</Typography>
        <ActionsBar
          activeFilters={activeFilters}
          setActiveFilters={setActiveFilters}
          filterOptions={formattedFilterOptions}
        />
      </Stack>
      <Stack width='100%' height='100%' mt={2} paddingBlock={2}>
        {formattedChartData && (
          <>
            {formattedChartData?.length > 0 ? (
              <EmployeeOverviewChart chartData={formattedChartData} />
            ) : (
              <Typography sx={styles.NoDataTitle}>No Data</Typography>
            )}
          </>
        )}
      </Stack>
    </Stack>
  )
}

export default OverviewTab
